import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Blade from '@/components/ui/Blade/Blade';
import { usePostAuthApiInfiniteQuery } from '@/hooks/UseApiCalls';
import apiClient from '@/api/ApiClient';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import downloadIcon from '@/assets/icons/download.svg';
import eyeIcon from '@/assets/icons/eye.svg';
import { PendingOfflineDonation, OfflineDonationStatus } from '@/pages/Donation/DonationsAdmin/types';
import { updateDestinationBankAccount } from '@/pages/Donation/PublicDonation/api';
import { DESTINATION_BANK_ACCOUNTS, findDestinationBank } from '@/pages/Donation/PublicDonation/destinationBankAccounts';
import { downloadProof } from './downloadFile';
import FiltersArea from './FiltersArea';
import useTableDensity from '@/hooks/useTableDensity';
import styles from './OfflineDonationsManagement.module.css';

const PAGINATED_URL = 'donations/admin/offline-donations/paginated';
const SEARCH_DEBOUNCE_MS = 300;
const DENSITY_STORAGE_KEY = 'table.density.offlineDonationsManagement';

const statusClass = (s: string) => {
  switch (s) {
    case 'Pending': return styles.statusPending;
    case 'InProgress': return styles.statusInProgress;
    case 'FutureCheck': return styles.statusFutureCheck;
    case 'Approved': return styles.statusApproved;
    case 'Rejected': return styles.statusRejected;
    default: return '';
  }
};

const OfflineDonationsManagementPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [selected, setSelected] = useState<PendingOfflineDonation | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingDestBank, setEditingDestBank] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [statusFilter, setStatusFilter] = useState<OfflineDonationStatus | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [fundId, setFundId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [pageSize, setPageSize] = useState(50);

  const queryParams = useMemo(() => ({
    searchTerm: debouncedSearch || undefined,
    statusFilter: statusFilter || undefined,
    typeFilter: typeFilter || undefined,
    fundId: fundId ?? undefined,
    dateFrom: dateRange?.from || undefined,
    dateTo: dateRange?.to || undefined,
    sortBy: 'CreatedAt',
    sortDirection: 'Desc',
  }), [debouncedSearch, statusFilter, typeFilter, fundId, dateRange]);

  const {
    data: response,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = usePostAuthApiInfiniteQuery<PendingOfflineDonation, typeof queryParams>(
    PAGINATED_URL,
    queryParams,
    pageSize
  );

  useEffect(() => {
    refetch();
  }, [pageSize, refetch]);

  const donations = useMemo(() => response?.pages.flatMap((p) => p.data) ?? [], [response]);

  const refresh = () => refetch();

  const doAction = async (url: string, body?: object) => {
    setActionBusy(true);
    setActionResult(null);
    try {
      await apiClient.post(url, body ?? {}, { headers });
      refresh();
      setActionResult({ type: 'success', message: t('donations.management.actionSuccess') });
    } catch {
      setActionResult({ type: 'error', message: t('donations.management.actionError') });
    } finally {
      setActionBusy(false);
    }
  };

  const markInProgress = (id: number) => doAction(`donations/pending/${id}/in-progress`);
  const returnToPending = (id: number) => doAction(`donations/pending/${id}/return-to-pending`);
  const approve = (id: number) => doAction(`donations/pending/${id}/approve`);
  const reject = (id: number) => {
    if (!rejectReason.trim()) return;
    doAction(`donations/pending/${id}/reject`, { reason: rejectReason }).then(() => {
      setRejectingId(null);
      setRejectReason('');
    });
  };

  const saveDestinationBank = async (id: number) => {
    if (!editingDestBank) return;
    const acc = DESTINATION_BANK_ACCOUNTS.find((a) => a.id === editingDestBank);
    if (!acc) return;
    setActionBusy(true);
    setActionResult(null);
    try {
      await updateDestinationBankAccount(id, acc.bankNumber, acc.branchNumber, acc.accountNumber);
      setEditingDestBank(null);
      refresh();
      setActionResult({ type: 'success', message: t('donations.management.actionSuccess') });
    } catch {
      setActionResult({ type: 'error', message: t('donations.management.actionError') });
    } finally {
      setActionBusy(false);
    }
  };

  const statusLabel = (s: string) => t(`donations.management.status.${s}`);
  const typeLabel = (type: string) =>
    t(`donations.methods.${type === 'BankTransfer' ? 'ManualBankTransfer' : 'Check'}`);
  const currencySymbol = (c: string) => c === 'USD' ? '$' : '₪';

  const columns: ColumnDef<PendingOfflineDonation>[] = [
    {
      id: 'createdAt',
      header: Col(t('donations.management.col.date')),
      accessorKey: 'createdAt',
      cell: Cell(cellType.Date),
      enableSorting: true,
    },
    {
      id: 'donorFullName',
      header: Col(t('donations.management.col.donor')),
      accessorKey: 'donorFullName',
      cell: ({ row }) => (
        <div className={styles.donorCell}>
          <span>{row.original.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</span>
          {(row.original.donorPhone || row.original.donorEmail) && (
            <span className={styles.donorCellSub}>
              {[row.original.donorPhone, row.original.donorEmail].filter(Boolean).join(' · ')}
            </span>
          )}
        </div>
      ),
      enableSorting: true,
    },
    {
      id: 'fund',
      header: Col(t('donations.management.col.fund')),
      accessorFn: (row) => row.fundName ? `${row.fundId} - ${row.fundName}` : String(row.fundId),
      enableSorting: true,
    },
    {
      id: 'type',
      header: Col(t('donations.management.col.type')),
      accessorFn: (row) => typeLabel(row.type),
      enableSorting: true,
    },
    {
      id: 'amount',
      header: Col(t('donations.management.col.amount')),
      accessorFn: (row) => `${currencySymbol(row.currency)}${row.amount.toLocaleString()}`,
      enableSorting: true,
    },
    {
      id: 'status',
      header: Col(t('donations.management.col.status')),
      accessorKey: 'status',
      cell: ({ row }) => (
        <span className={`${styles.statusBadge} ${statusClass(row.original.status)}`}>
          {statusLabel(row.original.status)}
        </span>
      ),
      enableSorting: true,
    },
  ];

  const exportColumns: ColumnDef<PendingOfflineDonation>[] = [
    { id: 'createdAt', header: t('donations.management.col.date'), accessorKey: 'createdAt' },
    { id: 'donorFullName', header: t('donations.management.col.donor'), accessorKey: 'donorFullName' },
    { id: 'donorPhone', header: t('phoneNumber'), accessorKey: 'donorPhone' },
    { id: 'donorEmail', header: t('email'), accessorKey: 'donorEmail' },
    { id: 'donorIsraeliId', header: t('donations.israeliId'), accessorKey: 'donorIsraeliId' },
    { id: 'fund', header: t('donations.management.col.fund'), accessorFn: (row) => row.fundName ? `${row.fundId} - ${row.fundName}` : String(row.fundId) },
    { id: 'type', header: t('donations.management.col.type'), accessorFn: (row) => typeLabel(row.type) },
    { id: 'amount', header: t('donations.management.col.amount'), accessorFn: (row) => `${currencySymbol(row.currency)}${row.amount.toLocaleString()}` },
    { id: 'bankNumber', header: t('donations.offline.bankNumber'), accessorKey: 'bankNumber' },
    { id: 'branchNumber', header: t('donations.offline.branchNumber'), accessorKey: 'branchNumber' },
    { id: 'accountNumber', header: t('donations.offline.accountNumber'), accessorKey: 'accountNumber' },
    { id: 'checkNumber', header: t('donations.offline.checkNumber'), accessorKey: 'checkNumber' },
    { id: 'checkSubType', header: t('donations.offline.checkSubType'), accessorFn: (row) => row.checkSubType ? t(`donations.offline.${row.checkSubType === 'Deposited' ? 'checkDeposited' : 'checkForDeposit'}`) : '' },
    { id: 'destinationBankAccount', header: t('donations.offline.destinationBankAccount'), accessorFn: (row) => findDestinationBank(row.destBankNumber, row.destBranchNumber, row.destAccountNumber)?.label ?? (row.destBankNumber ? `${row.destBankNumber}/${row.destBranchNumber}/${row.destAccountNumber}` : '') },
    { id: 'maturityDate', header: t('donations.offline.maturityDate'), accessorKey: 'maturityDate' },
    { id: 'status', header: t('donations.management.col.status'), accessorFn: (row) => statusLabel(row.status) },
    { id: 'rejectionReason', header: t('donations.management.rejectionReason'), accessorKey: 'rejectionReason' },
  ];

  const handleRowClick = (row: PendingOfflineDonation) => {
    setSelected(row);
    setRejectingId(null);
    setRejectReason('');
    setEditingDestBank(null);
    setActionResult(null);
  };

  const bladeHeaderTop = selected ? (
    <span className="body-lr-regular">
      {typeLabel(selected.type)}
      {' - '}
      <span>{selected.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</span>
    </span>
  ) : null;

  const bladeHeaderBottom = selected ? (
    <>
      <span className="body-sm-medium" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {currencySymbol(selected.currency)}{selected.amount.toLocaleString()}
      </span>
      <span className="body-sm-regular">
        {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(selected.createdAt))}
      </span>
    </>
  ) : null;

  return (
    <div>
      <DataTable
        title={t('donations.management.title')}
        data={donations}
        isLoading={isLoading || (isFetching && !isFetchingNextPage)}
        columns={columns}
        exportColumns={exportColumns}
        exportFilename={t('donations.management.title')}
        handleRowClick={handleRowClick}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="offlineDonationsManagement"
        searchPlaceholder="donations.management.search"
        customGlobalSearch={() => true}
        onLoadMore={fetchNextPage}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        filters={
          <FiltersArea
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            fundId={fundId}
            setFundId={setFundId}
            dateRange={dateRange}
            setDateRange={setDateRange}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        }
      />

      {selected && (
        <Blade
          handleXclick={() => setSelected(null)}
          width="456px"
          headerTop={bladeHeaderTop}
          headerBottom={bladeHeaderBottom}
        >
          <div className={`${styles.bladeContent} custom-scrollbar`}>
            {/* Status */}
            <section className={styles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('donations.management.col.status')}</h3>
              <div className="body-md-regular">
                <span className={`${styles.statusBadge} ${statusClass(selected.status)}`}>
                  {statusLabel(selected.status)}
                </span>
              </div>
            </section>

            <div className={styles.line} />

            {/* Fund */}
            <section className={styles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('donations.management.col.fund')}</h3>
              <div className="body-md-regular text-(--font-body-1)">
                <p>{selected.fundName ? `${selected.fundId} - ${selected.fundName}` : selected.fundId}</p>
              </div>
            </section>

            <div className={styles.line} />

            {/* Donor Details */}
            <section className={styles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">{t('donations.donorDetails')}</h3>
              <div className="body-md-regular text-(--font-body-1)">
                <p>{selected.donorFullName?.trim() || t('donations.admin.unnamedDonor')}</p>
                {selected.donorPhone && <p>{selected.donorPhone}</p>}
                {selected.donorEmail && <p>{selected.donorEmail}</p>}
                {selected.donorIsraeliId && <p>{t('donations.israeliId')}: {selected.donorIsraeliId}</p>}
              </div>
            </section>

            <div className={styles.line} />

            {/* Bank / Check Details */}
            <section className={styles.bladeSection}>
              <h3 className="body-md-semibold text-(--font-heading)">
                {selected.type === 'Check' ? t('donations.offline.checkDetails') : t('donations.offline.bankDetails')}
              </h3>
              <div className="body-md-regular text-(--font-body-2)">
                {selected.type === 'Check' && selected.checkSubType && (
                  <p>{t('donations.offline.checkSubType')}: {t(`donations.offline.${selected.checkSubType === 'Deposited' ? 'checkDeposited' : 'checkForDeposit'}`)}</p>
                )}
                {selected.bankNumber && <p>{t('donations.offline.bankNumber')}: {selected.bankNumber}</p>}
                {selected.branchNumber && <p>{t('donations.offline.branchNumber')}: {selected.branchNumber}</p>}
                {selected.accountNumber && <p>{t('donations.offline.accountNumber')}: {selected.accountNumber}</p>}
                {selected.checkNumber && <p>{t('donations.offline.checkNumber')}: {selected.checkNumber}</p>}
                {selected.maturityDate && <p>{t('donations.offline.maturityDate')}: {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(selected.maturityDate))}</p>}
              </div>
            </section>

            {/* Destination Bank Account */}
            {(selected.type === 'BankTransfer' || (selected.type === 'Check' && selected.checkSubType === 'Deposited')) && (
              <>
                <div className={styles.line} />
                <section className={styles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.offline.destinationBankAccount')}</h3>
                  {editingDestBank !== null ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <select
                        value={editingDestBank}
                        onChange={(e) => setEditingDestBank(e.target.value)}
                        disabled={actionBusy}
                      >
                        <option value="">{t('donations.offline.selectDestinationBank')}</option>
                        {DESTINATION_BANK_ACCOUNTS.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.label}</option>
                        ))}
                      </select>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className={styles.primaryBtn} onClick={() => saveDestinationBank(selected.id)} disabled={!editingDestBank?.trim() || actionBusy}>
                          {actionBusy ? t('loading') : t('save')}
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={() => setEditingDestBank(null)}>
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="body-md-regular text-(--font-body-2)">
                        {selected.destBankNumber
                          ? (findDestinationBank(selected.destBankNumber, selected.destBranchNumber, selected.destAccountNumber)?.label ?? `${selected.destBankNumber}/${selected.destBranchNumber}/${selected.destAccountNumber}`)
                          : t('donations.offline.notSet')}
                      </span>
                      {selected.status !== 'Approved' && selected.status !== 'Rejected' && (
                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => setEditingDestBank(findDestinationBank(selected.destBankNumber, selected.destBranchNumber, selected.destAccountNumber)?.id ?? '')}
                          style={{ padding: '4px 12px', fontSize: '13px' }}
                        >
                          {t('edit')}
                        </button>
                      )}
                    </div>
                  )}
                </section>
              </>
            )}

            {/* Proof Files */}
            {(selected.proofUrl || selected.secondProofUrl) && (
              <>
                <div className={styles.line} />
                <section className={styles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.offline.attachments')}</h3>
                  <div className={styles.proofLinks}>
                    {selected.proofUrl && (
                      <div className={styles.proofRow}>
                        <a href={selected.proofUrl} target="_blank" rel="noreferrer" className={styles.proofLink}>
                          📎 {selected.type === 'Check' ? t('donations.checkFront') : t('donations.proofFile')}
                        </a>
                        <div className={styles.proofActions}>
                          <a href={selected.proofUrl} target="_blank" rel="noreferrer" className={styles.downloadBtn} title={t('view')}>
                            <img src={eyeIcon} alt="" />
                          </a>
                          <button type="button" onClick={() => downloadProof(selected.id, 'proof', currentUser?.token ?? '')} className={styles.downloadBtn} title={t('download')}>
                            <img src={downloadIcon} alt="" />
                          </button>
                        </div>
                      </div>
                    )}
                    {selected.secondProofUrl && (
                      <div className={styles.proofRow}>
                        <a href={selected.secondProofUrl} target="_blank" rel="noreferrer" className={styles.proofLink}>
                          📎 {t('donations.checkBack')}
                        </a>
                        <div className={styles.proofActions}>
                          <a href={selected.secondProofUrl} target="_blank" rel="noreferrer" className={styles.downloadBtn} title={t('view')}>
                            <img src={eyeIcon} alt="" />
                          </a>
                          <button type="button" onClick={() => downloadProof(selected.id, 'second', currentUser?.token ?? '')} className={styles.downloadBtn} title={t('download')}>
                            <img src={downloadIcon} alt="" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            {selected.rejectionReason && (
              <>
                <div className={styles.line} />
                <section className={styles.bladeSection}>
                  <h3 className="body-md-semibold text-(--font-heading)">{t('donations.management.rejectionReason')}</h3>
                  <p className="body-md-regular" style={{ color: 'var(--status-rejected-text, #991b1b)' }}>{selected.rejectionReason}</p>
                </section>
              </>
            )}

            {/* Actions */}
            {(selected.status === 'Pending' || selected.status === 'InProgress' || selected.status === 'FutureCheck') && (
              <>
                <div className={styles.line} />
                <section className={styles.bladeSection}>
                  <div className={styles.detailActions}>
                    {(selected.status === 'Pending' || selected.status === 'FutureCheck') && (
                      <button type="button" className={styles.secondaryBtn} onClick={() => markInProgress(selected.id)} disabled={actionBusy}>
                        {t('donations.management.markInProgress')}
                      </button>
                    )}
                    {selected.status === 'InProgress' && (
                      <button type="button" className={styles.secondaryBtn} onClick={() => returnToPending(selected.id)} disabled={actionBusy}>
                        {t('donations.management.returnToPending')}
                      </button>
                    )}
                    <button type="button" className={styles.primaryBtn} onClick={() => approve(selected.id)} disabled={actionBusy}>
                      {actionBusy ? t('loading') : t('donations.management.approve')}
                    </button>
                    <button type="button" className={styles.outlineRedBtn} onClick={() => setRejectingId(selected.id)} disabled={actionBusy}>
                      {t('donations.management.reject')}
                    </button>
                  </div>

                  {rejectingId === selected.id && (
                    <div className={styles.rejectForm}>
                      <textarea
                        className={styles.rejectTextarea}
                        placeholder={t('donations.management.rejectReasonPlaceholder')}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      <div className={styles.rejectFormActions}>
                        <button type="button" className={styles.primaryBtn} onClick={() => reject(selected.id)} disabled={!rejectReason.trim() || actionBusy}>
                          {actionBusy ? t('loading') : t('donations.management.confirmReject')}
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={() => { setRejectingId(null); setRejectReason(''); }}>
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {actionResult && (
                    <div className={actionResult.type === 'success' ? styles.actionSuccess : styles.actionError}>
                      {actionResult.message}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        </Blade>
      )}
    </div>
  );
};

export default OfflineDonationsManagementPage;
